import { computed, ComputedRef, Ref, ref, watch } from '@vue/composition-api';
import {
  AdgroupFormData,
  CampaignFormData,
  LocationTargetingSearchScene,
  AreaItemOrigin,
  AreaItem,
  LocationInfo,
  LocationGeoType,
  AreaStatus,
} from '@tt4b/ttam-enums';
import { useRequest, useSearchLocationBffApi, useAdgroupLocationAvailable } from '@tt4b/ttam-hooks';
import { debounce } from 'lodash-es';
import { generateLocationTargetingAdContext, formatLocationItem } from '../utils';
import { useLocationTargetingDataSource } from './location-data-source';

const searchKeyword = ref<string>('');
const searchResult = ref<AreaItem[]>([]);
const associateTagsMap = ref({});

export function useLocationSearchResultDataSource(options: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  availableAreaList: ComputedRef<AreaItem[]>;
}) {
  const { adgroupData, campaignData, availableAreaList } = options;
  const { isZipcodeLocationAvailable } = useAdgroupLocationAvailable();
  const { isLoading, run } = useRequest(useSearchLocationBffApi, {} as any, {
    manual: true,
  });

  async function searchLocation(opt: { keywords: string[] }) {
    /**
     * 只能在用户可选的列表下搜索，如果为空不做搜索
     * 目前主要在电商和 Lead 汽车广告下可能为空，因为需要先选择 catalog/shop
     */
    if (availableAreaList.value.length === 0) {
      return;
    }

    const { keywords } = opt;
    const scene = LocationTargetingSearchScene.FUZZY;
    /**
     * countryCodes 代表哪些国家下进行搜索，如果做了过滤则将可选的国家传入
     * 1. 默认为 [] 可以不指定，服务端会查询用户可选的国家列表，然后在这些国家下进行搜索
     * 2. 如果传了指定 countryCodes 只会在指定的国家下搜索
     */
    const countryCodes = availableAreaList.value
      .filter(item => item.status === AreaStatus.ACTIVE)
      .map(item => item.code);
    const geoTypes = isZipcodeLocationAvailable.value
      ? Object.values(LocationGeoType).filter(v => typeof v === 'number')
      : Object.values(LocationGeoType).filter(v => typeof v === 'number' && v !== LocationGeoType.ZIP_CODE);
    const locationTargetingAdContext = generateLocationTargetingAdContext({
      adgroupData: adgroupData.value,
      campaignData: campaignData.value,
    });

    /**
     * 没有版位时，不调用搜索接口
     */
    if (!locationTargetingAdContext.inventoryFlow?.length) {
      return;
    }

    const searchLocationParam = {
      keywords,
      scene,
      queryParams: {
        countryCodes,
        geoTypes,
      },
      adContext: locationTargetingAdContext,
      pagination: {
        page: 0, // 从0开始。
        limit: 100, // 每个标签最多返回多少个匹配结果。
      },
    };
    const { result, resp_context: respContext } = await run(searchLocationParam);
    const { associate_tags: associateTags } = respContext;
    const { formatResultMap: formatResultMapAssociateTagsMap } = formatLocationItem({
      tags: associateTags,
      visible: false,
      isNotNeedFormatAssociateTags: true,
    });
    associateTagsMap.value = formatResultMapAssociateTagsMap;

    const formatResult: AreaItem[] = [];
    Object.values(result).forEach((list: Array<LocationInfo>) => {
      const { formatResult: formatResultItem } = formatLocationItem({
        tags: list,
        visible: true,
        associateTagsMap: formatResultMapAssociateTagsMap,
        isNotNeedFormatAssociateTags: true,
      });
      formatResult.push(...formatResultItem);
    });
    searchResult.value = formatResult;
  }

  const searchLocationByKeyword = debounce(
    (keyword: string) => {
      if (keyword) {
        searchLocation({
          keywords: [keyword],
        });
      } else {
        searchResult.value = [];
      }
    },
    500,
    { leading: false, trailing: true, maxWait: 500 },
  );

  function subscribeFuzzySearch() {
    watch(searchKeyword, value => {
      searchLocationByKeyword(value);
    });
  }

  const onSearchKeywordChange = val => {
    searchKeyword.value = val;
  };

  return {
    subscribeFuzzySearch,
    searchKeyword,
    onSearchKeywordChange,
    isLoading,
  };
}

export function useLocationSearchResultList() {
  const locationSearchResultList = computed(() => searchResult.value);
  const { areaMap, updateLocationData, virtualNodeIdParentIdMap } = useLocationTargetingDataSource();

  function checkIsNotFromAreaV2(id) {
    return !virtualNodeIdParentIdMap.value[id] && areaMap.value[id]?.origin !== AreaItemOrigin.AREAV2;
  }

  function handleLogSearchResult(locations) {
    const formatResult: AreaItem[] = [];
    const formatResultMap: Record<number, AreaItem> = {};
    locations.forEach(location => {
      const isNotComeFromAreaV2 = checkIsNotFromAreaV2(location.id);
      // 只额外存不是area_v2接口返回的数据
      if (isNotComeFromAreaV2) {
        formatResult.push({ ...location, visible: false });
        formatResultMap[location.id] = location;

        let { parentId } = location;

        while (parentId && !areaMap.value[parentId]) {
          if (checkIsNotFromAreaV2(parentId)) {
            formatResultMap[parentId] = associateTagsMap.value[parentId];
            formatResult.push(associateTagsMap.value[parentId]);
          }
          parentId = associateTagsMap.value[parentId]?.parentId;
        }
      }
    });

    updateLocationData({ list: formatResult, map: formatResultMap });
  }
  return {
    locationSearchResultList,
    handleLogSearchResult,
  };
}
